import React from "react"
import * as Yup from "yup"
import { useQuery, useMutation } from "react-query"
import { Formik } from "formik"
import { Container } from "@material-ui/core"
import Select from "react-select"
import { useParams } from "react-router-dom"
import { Button } from "../components"
import { fetchCurrenceis, fetchCountries } from "../services"
import { fetchDeal, updateDeal } from "../services/deal"

const typeOptions = [
  { value: "new", label: "New" },
  { value: "renewal", label: "Renewal" },
]

const selectStyles = {
  control: base => ({ ...base, height: 42, minHeight: 42 }),
}

function EditDeal() {
  const { id } = useParams()

  const { data: deal, isLoading, refetch } = useQuery(
    ["deal", id],
    () => fetchDeal(id),
    {
      select: res => res.deal,
      refetchOnWindowFocus: false,
    }
  )

  const { data: currencyOptions = [], isLoading: isCurrenciesLoading } = useQuery(
    "currencies",
    () => fetchCurrenceis(),
    {
      select: res => res.currencies?.map(e => ({ value: e.id, label: `${e.name} - ${e.code}` })),
      refetchOnWindowFocus: false,
    }
  )

  const { data: countryOptions = [], isLoading: isCountriesLoading } = useQuery(
    "countries",
    () => fetchCountries(),
    {
      select: res => res.countries?.map(e => ({ value: e.id, label: e.name })),
      refetchOnWindowFocus: false,
    }
  )

  const { mutate: saveDeal, isLoading: isSaving } = useMutation(
    data => updateDeal(id, data),
    {
      onSuccess: () => {
        alert("Deal updated successfully!")
        refetch()
      },
      onError: err => {
        alert(err?.data?.message || "Something went wrong")
      },
    }
  )

  if (isLoading) return <div>Loading...</div>

  return (
    <Container spacing={1} maxWidth="xl">
      <div className="content-wrapper mb-55">
        <h3 className="mb-20 is-flex fw7">Edit deal — {deal?.name}</h3>
        <Formik
          enableReinitialize
          initialValues={{
            name: deal?.name || "",
            type: deal?.type || "new",
            price: deal?.sales || "",
            currency_id: deal?.currency_id || null,
            country_id: deal?.country_id || null,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Please enter a deal name"),
            type: Yup.string().oneOf(["new", "renewal"]).required("Please select a type"),
            price: Yup.number()
              .typeError("Price must be a number")
              .positive("Price must be greater than 0")
              .required("Please enter a price"),
            currency_id: Yup.number().nullable().required("Please select a currency"),
            country_id: Yup.number().nullable().required("Please select a country"),
          })}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false)
            saveDeal(values)
          }}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>

              {/* Name + Type */}
              <div className="group is-flex fdr">
                <div className="group" style={{ width: "60%" }}>
                  <div className="teams-title mb-10">Deal Name*</div>
                  <input
                    name="name"
                    value={values.name}
                    className="stllr-input"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.name && touched.name && (
                    <span className="v-error">{errors.name}</span>
                  )}
                </div>

                <div className="group ml-20" style={{ width: "40%" }}>
                  <div className="teams-title mb-10">Type*</div>
                  <Select
                    name="type"
                    options={typeOptions}
                    value={typeOptions.find(o => o.value === values.type)}
                    onChange={({ value }) => setFieldValue("type", value)}
                    styles={selectStyles}
                  />
                  {errors.type && touched.type && (
                    <span className="v-error">{errors.type}</span>
                  )}
                </div>
              </div>

              <hr />

              {/* Price + Currency + Country */}
              <div className="group is-flex fdr">
                <div className="group" style={{ width: "30%" }}>
                  <div className="teams-title mb-10">Deal Price*</div>
                  <input
                    type="number"
                    name="price"
                    value={values.price}
                    className="stllr-input"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min="0"
                    step="0.01"
                  />
                  {errors.price && touched.price && (
                    <span className="v-error">{errors.price}</span>
                  )}
                </div>

                <div className="group ml-20" style={{ width: "35%" }}>
                  <div className="teams-title mb-10">Currency*</div>
                  <Select
                    name="currency_id"
                    options={currencyOptions}
                    value={currencyOptions.find(o => o.value === values.currency_id) || null}
                    isLoading={isCurrenciesLoading}
                    placeholder="Select currency"
                    onChange={({ value }) => setFieldValue("currency_id", value)}
                    styles={selectStyles}
                  />
                  {errors.currency_id && touched.currency_id && (
                    <span className="v-error">{errors.currency_id}</span>
                  )}
                </div>

                <div className="group ml-20" style={{ width: "35%" }}>
                  <div className="teams-title mb-10">Country*</div>
                  <Select
                    name="country_id"
                    options={countryOptions}
                    value={countryOptions.find(o => o.value === values.country_id) || null}
                    isLoading={isCountriesLoading}
                    placeholder="Select country"
                    onChange={({ value }) => setFieldValue("country_id", value)}
                    styles={selectStyles}
                  />
                  {errors.country_id && touched.country_id && (
                    <span className="v-error">{errors.country_id}</span>
                  )}
                </div>
              </div>

              <div className="group is-flex aic jcfe fdr mt-45">
                <Button
                  text="SAVE CHANGES"
                  type="submit"
                  disabled={isSubmitting || isSaving}
                  className="stllr-btn primary btn-lg"
                />
              </div>

            </form>
          )}
        </Formik>
      </div>
    </Container>
  )
}

export default EditDeal
